import { escapeHtml } from './escape.ts';
import type { Destinatario, RenderContext } from './types.ts';

/**
 * Segnaposto {{...}} nel testo delle email.
 *
 * Due regole, e sono quelle che rendono la cosa sicura invece che pericolosa:
 *
 *   1. LISTA CHIUSA. Un segnaposto che non e' in elenco diventa stringa vuota
 *      e finisce fra gli avvisi. Non si valuta niente, non si esegue niente.
 *
 *   2. OGNI VALORE VIENE ESCAPATO. La sostituzione avviene sull'HTML gia'
 *      pronto, quindi un contatto che si chiama "<script>" non puo' iniettare
 *      niente: diventa testo visibile.
 *
 * I segnaposto valgono per il TESTO, non per gli indirizzi dei link: dentro un
 * href un valore escapato non sarebbe un URL valido. Il link di disiscrizione
 * non e' un segnaposto, lo mette il piede della grafica.
 */

export type NomeTag =
  | 'nome'
  | 'nome_completo'
  | 'azienda'
  | 'email'
  | 'citta'
  | 'provincia'
  | 'azienda_mittente'
  | 'sito'
  | 'anno';

export const TAG_DISPONIBILI: ReadonlyArray<{ nome: NomeTag; descrizione: string; esempio: string }> = [
  { nome: 'nome', descrizione: 'Nome di battesimo', esempio: 'Mario' },
  { nome: 'nome_completo', descrizione: 'Nome e cognome', esempio: 'Mario Rossi' },
  { nome: 'azienda', descrizione: 'Azienda del destinatario', esempio: 'Rossi S.r.l.' },
  { nome: 'email', descrizione: 'Indirizzo del destinatario', esempio: 'mario@rossi.it' },
  { nome: 'citta', descrizione: 'Citta del destinatario', esempio: 'Pescara' },
  { nome: 'provincia', descrizione: 'Provincia del destinatario', esempio: 'PE' },
  { nome: 'azienda_mittente', descrizione: 'La nostra ragione sociale', esempio: 'SLAB S.r.l.' },
  { nome: 'sito', descrizione: 'Il nostro sito', esempio: 'www.esempio.it' },
  { nome: 'anno', descrizione: 'Anno corrente', esempio: String(new Date().getFullYear()) },
];

const REGEX_TAG = /\{\{\s*([a-z_.]+)\s*(?:\|([^}]*))?\}\}/g;

export interface EsitoMergeTag {
  testo: string;
  /** Segnaposto scritti male o inesistenti: vanno mostrati, non nascosti. */
  sconosciuti: string[];
  /** Segnaposto rimasti vuoti e senza valore di riserva. */
  vuoti: string[];
}

/** Primo pezzo del nome completo, quando il nome di battesimo non c'e'. */
function primoNome(destinatario: Destinatario): string {
  if (destinatario.nome) return destinatario.nome;
  const completo = (destinatario.nomeCompleto ?? '').trim();
  return completo ? completo.split(/\s+/)[0] : '';
}

function valoreDi(
  nome: string,
  destinatario: Destinatario,
  ctx: RenderContext
): string | undefined {
  if (nome.startsWith('campo.')) {
    const chiave = nome.slice('campo.'.length);
    const valore = destinatario.campi?.[chiave];
    return valore === undefined || valore === null ? '' : String(valore);
  }

  switch (nome as NomeTag) {
    case 'nome':
      return primoNome(destinatario);
    case 'nome_completo':
      return destinatario.nomeCompleto ?? '';
    case 'azienda':
      return destinatario.azienda ?? '';
    case 'email':
      return destinatario.email;
    case 'citta':
      return destinatario.citta ?? '';
    case 'provincia':
      return destinatario.provincia ?? '';
    case 'azienda_mittente':
      return ctx.branding.ragioneSociale;
    case 'sito':
      return ctx.branding.sito;
    case 'anno':
      return String(new Date().getFullYear());
    default:
      return undefined;
  }
}

export function applicaMergeTag(
  testo: string,
  destinatario: Destinatario,
  ctx: RenderContext,
  opzioni: { escapa?: boolean } = {}
): EsitoMergeTag {
  const escapa = opzioni.escapa !== false;
  const sconosciuti: string[] = [];
  const vuoti: string[] = [];

  const risultato = testo.replace(REGEX_TAG, (_intero, nome: string, riserva?: string) => {
    const valore = valoreDi(nome, destinatario, ctx);

    if (valore === undefined) {
      if (!sconosciuti.includes(nome)) sconosciuti.push(nome);
      return '';
    }

    const finale = valore.trim() !== '' ? valore : (riserva ?? '').trim();
    if (finale === '') {
      if (!vuoti.includes(nome)) vuoti.push(nome);
      return '';
    }

    return escapa ? escapeHtml(finale) : finale;
  });

  return { testo: risultato, sconosciuti, vuoti };
}

/** Elenco dei segnaposto usati in un testo, per il controllo prima dell'invio. */
export function tagUsati(testo: string): string[] {
  const trovati: string[] = [];
  for (const corrispondenza of testo.matchAll(REGEX_TAG)) {
    const nome = corrispondenza[1];
    if (!trovati.includes(nome)) trovati.push(nome);
  }
  return trovati;
}
